import type { HocuspocusProvider } from "@hocuspocus/provider";
import { Collaboration } from "@tiptap/extension-collaboration";
import { CollaborationCaret } from "@tiptap/extension-collaboration-caret";
import { Emoji } from "@tiptap/extension-emoji";
import { Heading } from "@tiptap/extension-heading";
import { Highlight } from "@tiptap/extension-highlight";
import { Image } from "@tiptap/extension-image";
import { Mathematics } from "@tiptap/extension-mathematics";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import { UniqueID } from "@tiptap/extension-unique-id";
import { CharacterCount, Placeholder, Selection } from "@tiptap/extensions";
import { StarterKit } from "@tiptap/starter-kit";
import type { Doc } from "yjs";
import { BlockquoteFigure } from "./tiptap-extension/BlockquoteFigure/";
import { Figcaption } from "./tiptap-extension/Figcaption/";
import { Figure } from "./tiptap-extension/Figure/";
import { UiState } from "./tiptap-extension/ui-state-extension";
import { Youtube } from "./tiptap-extension/Youtube";
import { HorizontalRule } from "./tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";

// for workspace
export const extensions = (props: {
  provider: HocuspocusProvider | null;
  ydoc: Doc | null;
  user?: { name: string; color: string };
  placeholder?: string;
  placeholderEmptyNodeClass?: string;
  onClickCommentThread?: (threadId: string | null) => void;
}) => [
  StarterKit.configure({
    undoRedo: props.ydoc ? false : undefined,
    horizontalRule: false,
    dropcursor: {
      width: 2,
    },
    link: {
      openOnClick: true,
      shouldAutoLink: () => false,
    },
    code: false,
    codeBlock: false,
  }),
  HorizontalRule,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  ...(props.ydoc ? [Collaboration.configure({ document: props.ydoc })] : []),
  ...(props.provider === null || props.user === undefined
    ? []
    : [
        CollaborationCaret.configure({
          provider: props.provider,
          user: { name: props.user.name, color: props.user.color },
        }),
      ]),
  Placeholder.configure({
    placeholder: props.placeholder,
    emptyNodeClass: props.placeholderEmptyNodeClass,
  }),
  // Mention,
  Emoji.configure({
    forceFallbackImages: false,
  }),
  Mathematics,
  Superscript,
  Subscript,
  Color.configure({
    types: [],
  }),
  TextStyle,
  // TaskList,
  // TaskItem.configure({ nested: true }),
  Highlight.configure({ multicolor: true }),
  Selection,
  UniqueID,
  Typography,
  UiState,
  CharacterCount,
];

// for content like stories, blogs, etc.
export const extensionsRich = (props: {
  provider: HocuspocusProvider | null;
  ydoc: Doc | null;
  user?: { name: string; color: string };
  placeholder?: string;
  placeholderEmptyNodeClass?: string;
  onClickCommentThread?: (threadId: string | null) => void;
}) => [
  StarterKit.configure({
    undoRedo: props.ydoc ? false : undefined,
    horizontalRule: false,
    dropcursor: {
      width: 2,
    },
    link: {
      openOnClick: true,
      shouldAutoLink: () => false,
    },
    code: false,
    codeBlock: false,
    heading: false,
  }),
  Heading, // this is replaced with custom heading extension that adds ids to headings when rendering
  HorizontalRule,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  ...(props.ydoc ? [Collaboration.configure({ document: props.ydoc })] : []),
  ...(props.provider === null || props.user === undefined
    ? []
    : [
        CollaborationCaret.configure({
          provider: props.provider,
          user: { name: props.user.name, color: props.user.color },
        }),
      ]),
  Placeholder.configure({
    placeholder: props.placeholder,
    emptyNodeClass: props.placeholderEmptyNodeClass,
  }),
  Color.configure({
    types: [],
  }),
  TextStyle,
  Highlight.configure({ multicolor: true }),
  Selection,
  UniqueID,
  Typography,
  UiState,
  Image,
  Figure,
  Figcaption,
  BlockquoteFigure,
  Youtube,
];

export const extensionsStoryComment = (props: {
  placeholder?: string;
  placeholderEmptyNodeClass?: string;
}) => [
  StarterKit.configure({
    undoRedo: false,
    horizontalRule: false,
    dropcursor: {
      width: 2,
    },
    link: {
      openOnClick: true,
      shouldAutoLink: () => false,
    },
    code: false,
    codeBlock: false,
  }),
  Placeholder.configure({
    placeholder: props.placeholder,
    emptyNodeClass: props.placeholderEmptyNodeClass,
  }),
];
